import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useApolloClient } from '@apollo/react-hooks';
import { ActivatePaymentMethodV2 as activatePaymentMethodMutation } from 'src/payments/graphql/mutations/activate-payment-method.gql';
import { useObserver } from 'mobx-react';
import { EnrollmentSteps } from 'src/payments/constants';
import ShowEnrollment from 'src/payments/graphql/queries/show-enrollment.gql';
import {
  ENROLLMENT_EVENTS,
  EVENT_ACTIONS,
  useDutchiePayAnalytics,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_DEFAULT_MESSAGES, ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';

const { success: successStep } = EnrollmentSteps;

type ActivatePaymentMethodResult = {
  activatePaymentMethod: () => Promise<void>;
};

export const useActivatePaymentMethod = (): ActivatePaymentMethodResult => {
  const apolloClient = useApolloClient();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const showErnie = useErnie();
  const refUrl = useObserver(() => DutchiePayEnrollment.refUrl);
  const connectedBankAccountDetails = useObserver(() => DutchiePayEnrollment.connectedBankAccountDetails);
  const { dutchiePayEventTracker, trackEnrollmentStepViewed } = useDutchiePayAnalytics();
  const activatePaymentMethod = async (): Promise<void> => {
    DutchiePayEnrollment.activateBankAccountRequested();
    const activateBankAccountProps = {
      paymentMethodId: connectedBankAccountDetails.id,
    };

    try {
      const response = await apolloClient.mutate({
        mutation: activatePaymentMethodMutation,
        variables: activateBankAccountProps,
        refetchQueries: [{ query: ShowEnrollment }],
        awaitRefetchQueries: true,
      });

      const success = response.data?.activatePaymentMethodV2?.genericResponse.success;

      if (response.errors || !success) {
        DutchiePayEnrollment.termsOfServiceError();
        showErnie(ERNIE_DEFAULT_MESSAGES.ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
        dutchiePayEventTracker(ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
          step: EnrollmentSteps.terms,
          action: EVENT_ACTIONS.FAILED,
        });
        return;
      }

      if (success) {
        DutchiePayEnrollment.activateBankAccountSucceeded();
        showErnie(`Your account information has been saved!`, ERNIE_TYPES.SUCCESS);
        trackEnrollmentStepViewed(EnrollmentSteps.completeEnrollment);
        if (!refUrl) {
          DutchiePayEnrollment.setCurrentStep(successStep);
        }
      }
    } catch (err) {
      DutchiePayEnrollment.termsOfServiceError();
      showErnie(ERNIE_DEFAULT_MESSAGES.ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
      dutchiePayEventTracker(ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
        step: EnrollmentSteps.terms,
        action: EVENT_ACTIONS.FAILED,
      });
      console.error(err);
    }
  };

  return {
    activatePaymentMethod,
  };
};
